import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSession } from "@clerk/clerk-react";
import { Button, Tooltip, Spinner, TableCell, SortDescriptor, SelectItem } from "@heroui/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { Document, Plus, ChevronDown, ChevronRight, Save } from "assets/icons";
import { SectionHeader, Select } from "..";
import { toast } from "react-toastify";
import { ScreenFileModal } from "./screenFileModal";
import { FileLogStatus } from "shared/types/file";
import SearchComponent, { SearchMode } from "shared/components/searchBar/components/searchComponent";
import FileLabelSearchComponent from "shared/components/searchBar/components/fileLabelSearchComponent";
import { ResourceType } from "shared/types/resource";
import { Lightbulb, X } from "@phosphor-icons/react";

interface SharePointItem {
  id: string;
  name: string;
  url?: string;
  webUrl?: string;
  path: string;
  isProcessed?: boolean;
  fileLog?: FileLog;
  fileLabel?: FileLabel;
}

interface FileLog {
  id: number;
  label: string[];
  documentDate?: Date;
  status: FileLogStatus;
  signStatus: string;
  statusDescription?: string;
  expirationDate?: Date;
  documentDigitalCompatibility?: string;
  summary?: string;
  sharePointFileId: string;
  sharePointFileUrl: string;
  unit?: any;
  building?: any;
  [key: string]: any; // For dynamic column access
}

interface FileLabel {
  id: number;
  label: string;
  resourceType?: 'building' | 'unit' | null;
  documentType?: 'document' | 'picture' | null;
  requiredFileLogColumns?: string[];
}

interface GroupedFiles {
  [path: string]: SharePointItem[];
}

const ITEMS_PER_PAGE = 10;

export interface AcquisitionDocumentsListProps {
  sharePointFolderId: string;
  sharePointFolderUrl: string;
  refetchDocuments?: any;
  acquisition?: any;
}

interface Building {
  id: string;
  googlePlaceLongAddress?: string;
  units?: Unit[];
}

interface Unit {
  id: string;
  name?: string;
}

const EditableCell = ({
  value,
  onEdit,
  type,
  options = [],
  buildings,
  isEditing,
  itemId,
  onEditStart,
}: {
  value: any;
  onEdit: (newValue: any) => void;
  type: 'label' | 'building' | 'unit' | 'status' | 'text' | 'textarea' | 'date';
  options?: any[];
  buildings?: Building[];
  isEditing: boolean;
  itemId: string;
  onEditStart: (id: string) => void;
}) => {
  const cellRef = useRef<HTMLDivElement>(null);

  const getDisplayValue = () => {
    if (!value && type === 'building' && buildings?.length === 1) {
      return buildings[0].id;
    }

    if (!value) return '-';

    switch (type) {
      case 'label':
        return typeof value === 'object' ? value.label : value;
      case 'building':
      case 'unit':
        return value.id;
      case 'status':
        return value;
      case 'date':
        return new Date(value).toLocaleDateString();
      default:
        return String(value);
    }
  };

  const renderEditControl = () => {
    switch (type) {
      case 'label':
        return (
          <FileLabelSearchComponent
            incomingSelectedLabel={typeof value === 'object' ? value.label : value}
            onSelect={(label) => {
              onEdit(label);
            }}
            incomingResourceTypes={["building", "unit"]}
            incomingDocumentTypes={["document", "picture"]}
            labelType="full"
          />
        );
      case 'building':
        if (!buildings?.length) {
          return (
            <SearchComponent
              editMode={true}
              label="Gebouw"
              onSelect={(building) => {
                onEdit(building);
              }}
              resourceFilter={[ResourceType.BUILDING]}
              initialValue={value?.id || ""}
              mode={SearchMode.SEARCH_AND_PRESENT}
            />
          );
        }

        // If there's only one building, show it as selected by default
        const defaultValue = buildings.length === 1 ? buildings[0].id : value?.id || "";

        return (
          <Select
            value={defaultValue}
            onChange={(e) => {
              const selectedBuilding = buildings.find(b => b.id === e.target.value);
              onEdit(selectedBuilding);
            }}
          >
            {[
              <SelectItem key="">Selecteer een gebouw</SelectItem>,
              ...buildings.map((building) => (
                <SelectItem key={building.id}>
                  {building.id}
                </SelectItem>
              ))
            ]}
          </Select>
        );
      case 'unit':
        const currentBuilding = options?.[0]?.buildingId ?
          buildings?.find(b => b.id === options[0].buildingId) :
          null;

        if (!currentBuilding?.units?.length) {
          return null;
        }

        return (
          <Select
            value={value?.id || ""}
            onChange={(e) => {
              const selectedUnit = currentBuilding?.units?.find(u => u.id === e.target.value);
              onEdit(selectedUnit);
            }}
          >
            {[
              <SelectItem key="">Select a unit</SelectItem>,
              ...currentBuilding.units.map((unit) => (
                <SelectItem key={unit.id}>
                  {unit.id}
                </SelectItem>
              ))
            ]}
          </Select>
        );
      case 'status':
        return (
          <Select
            value={value}
            onChange={(e) => {
              onEdit(e.target.value);
            }}
          >
            {Object.values(FileLogStatus).map((status) => (
              <SelectItem key={status}>
                {status}
              </SelectItem>
            ))}
          </Select>
        );
      case 'text':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onEdit(e.target.value)}
            className="w-full p-2 border rounded-sm focus:outline-hidden focus:ring-2 focus:ring-primary"
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
        );
      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => onEdit(e.target.value)}
            className="w-full p-2 border rounded-sm focus:outline-hidden focus:ring-2 focus:ring-primary min-h-[100px]"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            autoFocus
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={value ? new Date(value).toISOString().split('T')[0] : ''}
            onChange={(e) => {
              onEdit(e.target.value);
            }}
            className="w-full p-2 border rounded-sm focus:outline-hidden focus:ring-2 focus:ring-primary"
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
        );
    }
  };

  return (
    <div
      className="relative group editable-cell select-none min-h-[40px] w-full"
      ref={cellRef}
      onClick={(e) => {
        e.stopPropagation();
        if (!isEditing) {
          onEditStart(itemId);
        }
      }}
    >
      {isEditing ? (
        <div
          className="absolute inset-0 bg-white shadow-lg rounded-lg p-2 z-20"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {renderEditControl()}
        </div>
      ) : (
        <div className="h-full w-full cursor-pointer hover:bg-gray-50 rounded-sm flex items-center justify-between gap-2 p-2">
          <span className="truncate flex-1">{getDisplayValue()}</span>
        </div>
      )}
    </div>
  );
};

const AcquisitionDocumentsList = ({
  sharePointFolderId,
  sharePointFolderUrl,
  refetchDocuments,
  acquisition,
}: AcquisitionDocumentsListProps) => {
  const [unprocessedFiles, setUnprocessedFiles] = useState<GroupedFiles>({});
  const [partiallyProcessedFiles, setPartiallyProcessedFiles] = useState<GroupedFiles>({});
  const [processedFiles, setProcessedFiles] = useState<GroupedFiles>({});
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<SharePointItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    unprocessed: true,
    partial: true,
    processed: true
  });
  const [pagination, setPagination] = useState({
    unprocessed: { page: 1, hasMore: true },
    partial: { page: 1, hasMore: true },
    processed: { page: 1, hasMore: true }
  });
  const [totals, setTotals] = useState({
    unprocessed: 0,
    partial: 0,
    processed: 0
  });
  const [nextLinks, setNextLinks] = useState({
    unprocessed: null,
    partial: null,
    processed: null
  });
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "name",
    direction: "ascending",
  });
  const { session } = useSession();

  // Define an interface for the quick edit values
  interface QuickEditValues {
    label: string | null;
    building: any | null;
    unit: any | null;
    status: FileLogStatus;
    documentDate?: string;
    expirationDate?: string;
    statusDescription?: string;
    summary?: string;
  }

  // Update the state definition
  const [quickEditValuesPerStatus, setQuickEditValuesPerStatus] = useState<Record<'unprocessed' | 'partial' | 'processed', QuickEditValues>>({
    unprocessed: {
      label: null,
      building: null,
      unit: null,
      status: FileLogStatus.UNKNOWN,
      documentDate: undefined,
      expirationDate: undefined,
      statusDescription: undefined,
      summary: undefined
    },
    partial: {
      label: null,
      building: null,
      unit: null,
      status: FileLogStatus.UNKNOWN,
      documentDate: undefined,
      expirationDate: undefined,
      statusDescription: undefined,
      summary: undefined
    },
    processed: {
      label: null,
      building: null,
      unit: null,
      status: FileLogStatus.UNKNOWN,
      documentDate: undefined,
      expirationDate: undefined,
      statusDescription: undefined,
      summary: undefined
    }
  });

  const [currentStatus] = useState<'unprocessed' | 'partial' | 'processed'>('unprocessed');

  const buildings = useMemo(() =>
    (acquisition?.buildings || []) as Building[],
    [acquisition?.buildings]
  );

  const [localEdits, setLocalEdits] = useState<Record<string, {
    label?: string;
    building?: any;
    unit?: any;
    documentDate?: string;
    expirationDate?: string;
    status?: FileLogStatus;
    statusDescription?: string;
    summary?: string;
  }>>({});

  const [editingRowId, setEditingRowId] = useState<string | null>(null);

  const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);

  const saveAllChanges = async (status: 'unprocessed' | 'partial' | 'processed') => {
    try {
      console.log(unsavedChanges);
      const currentItems = getCurrentItems(status);
      const itemsToUpdate = currentItems
        .filter(item => localEdits[item.id])
        .map(item => ({
          id: item.id,
          changes: localEdits[item.id]
        }));

      await Promise.all(itemsToUpdate.map(({ id, changes }) => {
        console.log(changes)
        console.log(id)
        // fetchWithAuth(
        //   `${API_URL}/file/screen`,
        //   {
        //     method: "POST",
        //     headers: { 'Content-Type': 'application/json' },
        //     body: JSON.stringify({
        //       sharePointFileId: id,
        //       ...changes,
        //       acquisitionId: acquisition?.id
        //     })
        //   },
        //   session
        // )
      }
      ));

      // Clear local edits only for the current status
      setLocalEdits(prev => {
        const newEdits = { ...prev };
        currentItems.forEach(item => {
          delete newEdits[item.id];
        });
        return newEdits;
      });

      toast.success('Changes saved successfully');

      // Refresh only the current status
      await fetchPagedDocuments(pagination[status].page, status);
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('Failed to save changes');
    }
  };

  const fetchPagedDocuments = async (page: number, status: 'unprocessed' | 'partial' | 'processed') => {
    try {
      const skip = (page - 1) * ITEMS_PER_PAGE;
      const url = `${API_URL}/file/categorized-files/${sharePointFolderId}?acquisitionId=${acquisition?.id}&skip=${skip}&take=${ITEMS_PER_PAGE}${nextLinks[status] ? `&nextLink=${encodeURIComponent(nextLinks[status] as any)}` : ''
        }`;

      const response = await fetchWithAuth(url, { method: "GET" }, session);
      const { unprocessed, partial, processed, total, nextLink } = await response.json();

      // Update state based on status, replacing the previous state instead of merging
      if (status === 'unprocessed') {
        setUnprocessedFiles(groupByPath(unprocessed));
        setTotals(prev => ({ ...prev, unprocessed: total }));
      } else if (status === 'partial') {
        setPartiallyProcessedFiles(groupByPath(partial));
        setTotals(prev => ({ ...prev, partial: total }));
      } else {
        setProcessedFiles(groupByPath(processed));
        setTotals(prev => ({ ...prev, processed: total }));
      }

      setNextLinks(prev => ({
        ...prev,
        [status]: nextLink
      }));
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Failed to fetch documents");
    }
  };

  const loadMoreFiles = async (status: 'unprocessed' | 'partial' | 'processed') => {
    const nextPage = pagination[status].page + 1;
    await fetchPagedDocuments(nextPage, status);
    setPagination(prev => ({
      ...prev,
      [status]: {
        page: nextPage,
        hasMore: nextPage * ITEMS_PER_PAGE < totals[status]
      }
    }));
  };

  const [hasLoadedData, setHasLoadedData] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!sharePointFolderId || hasLoadedData) return;

      setIsLoading(true);
      try {
        await Promise.all([
          fetchPagedDocuments(1, 'unprocessed'),
          fetchPagedDocuments(1, 'partial'),
          fetchPagedDocuments(1, 'processed')
        ]);
        setHasLoadedData(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [sharePointFolderId, acquisition?.id, session, hasLoadedData]);

  useEffect(() => {
    return () => {
      setHasLoadedData(false); // Reset the flag when component unmounts
    };
  }, []);

  const handleManualRefresh = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchPagedDocuments(1, 'unprocessed'),
        fetchPagedDocuments(1, 'partial'),
        fetchPagedDocuments(1, 'processed')
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const groupByPath = (items: SharePointItem[]): GroupedFiles => {
    const uniqueItems = new Map<string, SharePointItem>();

    // First, collect all unique items
    items.forEach(item => {
      if (!uniqueItems.has(item.id)) {
        uniqueItems.set(item.id, item);
      }
    });

    // Then group the unique items by path
    return Array.from(uniqueItems.values()).reduce((groups: GroupedFiles, item) => {
      const path = item.path === "Root" ? "/" : item.path || '/';
      if (!groups[path]) {
        groups[path] = [];
      }
      groups[path].push(item);
      return groups;
    }, {});
  };

  const toggleSection = (section: 'unprocessed' | 'partial' | 'processed') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getCurrentItems = (status: 'unprocessed' | 'partial' | 'processed') => {
    const files = status === 'unprocessed'
      ? unprocessedFiles
      : status === 'partial'
        ? partiallyProcessedFiles
        : processedFiles;

    return Object.entries(files).flatMap(([path, items]) =>
      items.map(item => ({ ...item, path }))
    );
  };

  const renderFilesList = (files: Record<string, SharePointItem[]>, title: string, status: "unprocessed" | "partial" | "processed") => {
    if (Object.keys(files).length === 0) return null;

    const allItems = Object.entries(files).flatMap(([path, items]) =>
      items.map(item => ({ ...item, path }))
    );

    const sortedItems = [...allItems].sort((a, b) => {
      const first = a[sortDescriptor.column as keyof SharePointItem];
      const second = b[sortDescriptor.column as keyof SharePointItem];
      const firstValue = first?.toString() || '';
      const secondValue = second?.toString() || '';
      return sortDescriptor.direction === "ascending"
        ? firstValue.localeCompare(secondValue)
        : secondValue.localeCompare(firstValue);
    });

    const columns = [
      { key: "status", label: "Status", allowsSorting: true },
      { key: "name", label: "Bestandsnaam", allowsSorting: true },
      { key: "path", label: "Pad", allowsSorting: true },
      { key: "label", label: "Label", allowsSorting: false },
      { key: "building", label: "Gebouw", allowsSorting: false },
      { key: "unit", label: "Unit", allowsSorting: false },
      { key: "documentDate", label: "Document datum", allowsSorting: true },
      { key: "expirationDate", label: "Vervaldatum", allowsSorting: true },
      { key: "statusDescription", label: "Status beschrijving", allowsSorting: false },
      { key: "summary", label: "Samenvatting", allowsSorting: false },
      { key: "missingFields", label: "Ontbrekende velden", allowsSorting: false },
      { key: "actions", label: "", allowsSorting: false }
    ];

    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => toggleSection(status)}
            >
              {expandedSections[status] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <h3 className="text-lg font-semibold">{title}</h3>
              <span className="text-sm text-gray-500">({allItems.length})</span>
            </div>
            {hasUnsavedChangesForStatus(status) && (
              <span className="text-sm text-orange-500 italic ml-4">
                Je hebt onopgeslagen wijzigingen
              </span>
            )}
          </div>
          {hasUnsavedChangesForStatus(status) && (
            <Button
              color="primary"
              startContent={<Save className="h-4 w-4" />}
              onClick={() => saveAllChanges(status)}
            >
              Wijzigingen opslaan
            </Button>
          )}
        </div>

        {expandedSections[status] && (
          <>
            {renderQuickEditTools(status)}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-neutral-200">
                <thead>
                  <tr className="bg-neutral-50">
                    <th className="w-10 p-2 border-b border-r border-neutral-200">
                      <input
                        type="checkbox"
                        checked={sortedItems.every(item => selectedFiles.has(item.id))}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Select all items
                            setSelectedFiles(new Set(sortedItems.map(item => item.id)));
                          } else {
                            // Deselect all items
                            setSelectedFiles(new Set());
                          }
                        }}
                        className="rounded-sm border-neutral-300"
                      />
                    </th>
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className="p-2 text-left border-b border-r border-neutral-200"
                        onClick={() => {
                          if (column.allowsSorting) {
                            setSortDescriptor({
                              column: column.key,
                              direction: sortDescriptor.direction === "ascending" ? "descending" : "ascending"
                            });
                          }
                        }}
                      >
                        {column.label}
                        {column.allowsSorting && (
                          <span className="ml-1">
                            {sortDescriptor.column === column.key && (
                              sortDescriptor.direction === "ascending" ? "↑" : "↓"
                            )}
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item) => (
                    <tr
                      key={item.id}
                      className={`hover:bg-neutral-50 ${editingRowId === item.id ? 'bg-neutral-50' : ''}`}
                    >
                      <td
                        className={`w-10 p-2 border-b border-r border-neutral-200 relative ${localEdits[item.id] ? 'border-l-4 border-l-orange-500' : ''
                          }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedFiles.has(item.id)}
                          onChange={(e) => {
                            const newSelection = new Set(selectedFiles);
                            if (e.target.checked) {
                              newSelection.add(item.id);
                            } else {
                              newSelection.delete(item.id);
                            }
                            setSelectedFiles(newSelection);
                          }}
                          className="rounded-sm border-neutral-300"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className={`p-0 border-b border-r border-neutral-200 ${column.key === 'statusDescription' || column.key === 'summary' ? 'min-w-[200px]' : ''
                            }`}
                        >
                          {renderTableCell(item, column, status)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {pagination[status].hasMore && (
          <div className="flex justify-center mt-4">
            <Button
              size="sm"
              variant="light"
              onClick={() => loadMoreFiles(status)}
            >
              Laad meer
            </Button>
          </div>
        )}
      </div>
    );
  };

  const handleProcessFiles = async (files: SharePointItem[]) => {
    const currentItems = getCurrentItems(
      files[0]?.fileLog
        ? files[0].fileLog.status === FileLogStatus.UNKNOWN
          ? 'partial'
          : 'processed'
        : 'unprocessed'
    );

    const selectedItems = Array.from(selectedFiles).map(id =>
      currentItems.find(item => item.id === id)
    ).filter((item): item is SharePointItem => item !== undefined);

    const filesToProcess = files.length ? files : selectedItems;
    if (!filesToProcess.length) return;

    setSelectedFile(filesToProcess[0]); // For now, only process the first file
    setIsUploadModalOpen(true);

    let shouldRefetch = true;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && shouldRefetch) {
        handleManualRefresh();
        shouldRefetch = false;
      }
    };

    // Only add visibility change listener if we've loaded data
    if (hasLoadedData) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  };

  const handleFileOpen = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();  // Stop event bubbling
    e.preventDefault();    // Prevent default navigation
    window.open(e.currentTarget.href, '_blank'); // Open in new tab manually
  };

  const applyToSelected = async (values: Partial<QuickEditValues>, status: 'unprocessed' | 'partial' | 'processed') => {
    const currentItems = getCurrentItems(status);
    const selectedItems = Array.from(selectedFiles)
      .map(id => currentItems.find(item => item.id === id))
      .filter((item): item is SharePointItem => item !== undefined);

    if (!selectedItems.length) return;

    try {
      // Update local edits for all selected items
      const newEdits = { ...localEdits };

      selectedItems.forEach(item => {
        newEdits[item.id] = {
          ...newEdits[item.id],
          ...Object.entries(values).reduce((acc, [field, value]) => {
            // Handle special cases for each field type
            switch (field) {
              case 'label':
                acc[field] = value && typeof value === 'object' && 'label' in value ? value.label : value;
                break;
              case 'building':
              case 'unit':
                acc[field] = value;
                break;
              case 'documentDate':
              case 'expirationDate':
                acc[field] = value ? new Date(value as string).toISOString() : null;
                break;
              default:
                acc[field] = value;
            }
            return acc;
          }, {} as Record<string, any>)
        };
      });

      setLocalEdits(newEdits);
      setUnsavedChanges(true);
    } catch (error) {
      console.error('Error updating files:', error);
      toast.error('Failed to update files');
    }
  };

  // Add this function to handle Ella's advice
  const getEllaAdvice = async (fileIds: string[]) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/file/ella-advice`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fileIds,
            acquisitionId: acquisition?.id
          })
        },
        session
      );

      const adviceData = await response.json();

      // Update local edits with Ella's suggestions
      const newEdits = { ...localEdits };
      adviceData.forEach((advice: any) => {
        newEdits[advice.fileId] = {
          ...newEdits[advice.fileId],
          label: advice.label,
          documentDate: advice.documentDate,
          expirationDate: advice.expirationDate,
          statusDescription: advice.statusDescription,
          summary: advice.summary
        };
      });

      setLocalEdits(newEdits);
      setUnsavedChanges(true);
      toast.success('Ella heeft suggesties gedaan voor de geselecteerde bestanden');
    } catch (error) {
      console.error('Error getting Ella advice:', error);
      toast.error('Kon geen advies krijgen van Ella');
    }
  };

  // Update the renderQuickEditTools to include the Ella button
  const renderQuickEditTools = (status: 'unprocessed' | 'partial' | 'processed') => {
    // Only show tools if there are selected items in this status
    const selectedItemsInStatus = Array.from(selectedFiles).filter(id =>
      getCurrentItems(status).some(item => item.id === id)
    );

    if (!selectedItemsInStatus.length) return null;

    const quickEditValues = quickEditValuesPerStatus[status];

    // Properly type the setQuickEditValues function
    const setQuickEditValues = (newValues: QuickEditValues | ((prev: QuickEditValues) => QuickEditValues)) => {
      setQuickEditValuesPerStatus(prev => ({
        ...prev,
        [status]: typeof newValues === 'function' ? newValues(prev[status]) : newValues
      }));
    };

    return (
      <div className="flex items-center gap-4 mb-4 p-4 bg-white rounded-lg shadow">
        <span className="text-sm font-medium">
          {selectedItemsInStatus.length} file(s) selected
        </span>

        <Button
          onClick={() => getEllaAdvice(selectedItemsInStatus)}
          className="ml-auto bg-brick-primary text-white"
          startContent={<Lightbulb className="h-4 w-4" />}
        >
          Roep Ella in
        </Button>

        <FileLabelSearchComponent
          incomingSelectedLabel={quickEditValues.label}
          onSelect={(label) => {
            const updates = { label };
            setQuickEditValues({ ...quickEditValues, ...updates });
            applyToSelected(updates, status);
          }}
          incomingResourceTypes={["building", "unit"]}
          incomingDocumentTypes={["document", "picture"]}
          labelType="full"
        />

        {buildings?.length ? (
          <Select
            label="Gebouw"
            value={quickEditValues.building?.id || ""}
            onChange={(e) => {
              const building = buildings.find(b => b.id === e.target.value);
              const updates = { building };
              setQuickEditValues((prev: QuickEditValues) => ({ ...prev, ...updates }));
              applyToSelected(updates, status);
            }}
            className="w-48"
          >
            {[
              <SelectItem key="">Select a building</SelectItem>,
              ...buildings.map((building) => (
                <SelectItem key={building.id}>
                  {building.id}
                </SelectItem>
              ))
            ]}
          </Select>
        ) : (
          <SearchComponent
            editMode={true}
            label="Gebouw"
            onSelect={(building) => {
              const updates = { building };
              setQuickEditValues(prev => ({ ...prev, ...updates }));
              applyToSelected(updates, status);
            }}
            resourceFilter={[ResourceType.BUILDING]}
            initialValue={quickEditValues.building?.id || ""}
            mode={SearchMode.SEARCH_AND_PRESENT}
          />
        )}

        {quickEditValues.building && (
          <Select
            label="Unit"
            value={quickEditValues.unit?.id || ""}
            onChange={(e) => {
              const unit = quickEditValues.building.units?.find((u: any) => u.id === e.target.value);
              const updates = { unit };
              setQuickEditValues(prev => ({ ...prev, ...updates }));
              applyToSelected(updates, status);
            }}
            className="w-48"
          >
            {[
              <SelectItem key="">Select a unit</SelectItem>,
              ...(quickEditValues.building.units || []).map((unit: any) => (
                <SelectItem key={unit.id}>
                  {unit.id}
                </SelectItem>
              ))
            ]}
          </Select>
        )}

        <Select
          label="Status"
          value={quickEditValues.status}
          onChange={(e) => {
            const newStatus = e.target.value as FileLogStatus;
            const updates = { status: newStatus };
            setQuickEditValues(prev => ({ ...prev, ...updates }));
            applyToSelected(updates, status); // Here 'status' is the table status parameter
          }}
          className="w-48"
        >
          {Object.values(FileLogStatus).map((statusValue) => (
            <SelectItem key={statusValue}>
              {statusValue}
            </SelectItem>
          ))}
        </Select>

        <input
          type="date"
          value={quickEditValues.documentDate || ''}
          onChange={(e) => {
            const documentDate = e.target.value;
            const updates = { documentDate };
            setQuickEditValues((prev: QuickEditValues) => ({ ...prev, ...updates }));
            applyToSelected(updates, status);
          }}
          className="px-3 py-2 border rounded-md"
          placeholder="Document datum"
        />

        <input
          type="date"
          value={quickEditValues.expirationDate || ''}
          onChange={(e) => {
            const expirationDate = e.target.value;
            const updates = { expirationDate };
            setQuickEditValues((prev: QuickEditValues) => ({ ...prev, ...updates }));
            applyToSelected(updates, status);
          }}
          className="px-3 py-2 border rounded-md"
          placeholder="Vervaldatum"
        />

        <textarea
          value={quickEditValues.statusDescription || ''}
          onChange={(e) => {
            const statusDescription = e.target.value;
            const updates = { statusDescription };
            setQuickEditValues((prev: QuickEditValues) => ({ ...prev, ...updates }));
            applyToSelected(updates, status);
          }}
          className="px-3 py-2 border rounded-md"
          placeholder="Status beschrijving"
          rows={2}
        />

        <textarea
          value={quickEditValues.summary || ''}
          onChange={(e) => {
            const summary = e.target.value;
            const updates = { summary };
            setQuickEditValues((prev: QuickEditValues) => ({ ...prev, ...updates }));
            applyToSelected(updates, status);
          }}
          className="px-3 py-2 border rounded-md"
          placeholder="Samenvatting"
          rows={2}
        />
      </div>
    );
  };

  const handleCellEdit = async (fileId: string, field: string, value: any) => {
    setLocalEdits(prev => ({
      ...prev,
      [fileId]: {
        ...prev[fileId],
        [field]: value
      }
    }));
    setUnsavedChanges(true);
  };

  const renderTableCell = (item: SharePointItem, column: { key: string }, status: "unprocessed" | "partial" | "processed") => {
    const isEditing = editingRowId === item.id;
    const localEdit = localEdits[item.id];
    const getValueWithLocalEdit = (field: string) => {
      if (localEdit && field in localEdit) {
        return localEdit[field as keyof typeof localEdit];
      }
      if (field === 'label') {
        return item.fileLabel;
      }
      if (field === 'building') {
        // Return the default building if there's only one and no building is set
        if (!item.fileLog?.building && buildings?.length === 1) {
          return buildings[0];
        }
        return item.fileLog?.building;
      }
      if (field === 'unit') {
        return item.fileLog?.unit;
      }
      return item.fileLog?.[field] || item[field as keyof SharePointItem];
    };

    const preventSelection = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
    };

    switch (column.key) {
      case "status":
        return (
          <Tooltip
            content={
              status === "unprocessed"
                ? "Dit document moet nog verwerkt worden"
                : status === "partial"
                  ? "Dit document is gedeeltelijk verwerkt"
                  : "Dit document is volledig verwerkt"
            }
            placement="left"
          >
            <div
              className={`w-2 h-2 rounded-full cursor-help ${status === "unprocessed"
                ? 'bg-red-500'
                : status === "partial"
                  ? 'bg-orange-500'
                  : 'bg-green-500'
                }`}
            />
          </Tooltip>
        );
      case "name":
        return (
          <div className="flex items-center gap-2">
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium hover:text-primary"
              onClick={handleFileOpen}
            >
              {item.name}
            </a>
          </div>
        );
      case "path":
        return item.path;
      case "label":
        return (
          <EditableCell
            value={getValueWithLocalEdit('label') || item.fileLabel?.label || ''}
            onEdit={(label) => handleCellEdit(item.id, 'label', label)}
            type="label"
            buildings={buildings}
            isEditing={isEditing}
            itemId={item.id}
            onEditStart={setEditingRowId}
          />
        );
      case "building":
        return (
          <EditableCell
            value={getValueWithLocalEdit('building') || item.fileLog?.building || ''}
            onEdit={(building) => handleCellEdit(item.id, 'building', building)}
            type="building"
            buildings={buildings}
            isEditing={isEditing}
            itemId={item.id}
            onEditStart={setEditingRowId}
          />
        );
      case "unit":
        // Only show unit selection if the label's resourceType is 'unit'
        const shouldShowUnitSelect = item.fileLabel?.resourceType === 'unit';
        const currentBuilding = getValueWithLocalEdit('building') || item.fileLog?.building;

        return (
          shouldShowUnitSelect ?
            <EditableCell
              value={getValueWithLocalEdit('unit') || item.fileLog?.unit || ''}
              onEdit={(unit) => handleCellEdit(item.id, 'unit', unit)}
              type="unit"
              buildings={buildings}
              options={[{ buildingId: currentBuilding?.id }]}
              isEditing={isEditing}
              itemId={item.id}
              onEditStart={setEditingRowId}
            />
            : <TableCell onClick={preventSelection}>-</TableCell>);
      case "documentDate":
        return (
          <EditableCell
            value={getValueWithLocalEdit('documentDate') || item.fileLog?.documentDate || ''}
            onEdit={(value) => handleCellEdit(item.id, 'documentDate', value)}
            type="date"
            isEditing={isEditing}
            itemId={item.id}
            onEditStart={setEditingRowId}
          />
        );
      case "expirationDate":
        return (
          <EditableCell
            value={getValueWithLocalEdit('expirationDate') || item.fileLog?.expirationDate || ''}
            onEdit={(value) => handleCellEdit(item.id, 'expirationDate', value)}
            type="date"
            isEditing={isEditing}
            itemId={item.id}
            onEditStart={setEditingRowId}
          />
        );
      case "statusDescription":
        return (
          <EditableCell
            value={getValueWithLocalEdit('statusDescription') || item.fileLog?.statusDescription || ''}
            onEdit={(value) => handleCellEdit(item.id, 'statusDescription', value)}
            type="textarea"
            isEditing={isEditing}
            itemId={item.id}
            onEditStart={setEditingRowId}
          />
        );
      case "summary":
        return (
          <EditableCell
            value={getValueWithLocalEdit('summary') || item.fileLog?.summary || ''}
            onEdit={(value) => handleCellEdit(item.id, 'summary', value)}
            type="textarea"
            isEditing={isEditing}
            itemId={item.id}
            onEditStart={setEditingRowId}
          />
        );
      case "missingFields":
        return status === 'partial' ? (
          <TableCell onClick={preventSelection}>
            <div>
              {(item.fileLabel?.requiredFileLogColumns || []).filter((column: string) => {
                const value = item.fileLog?.[column];
                return !value || value === 'UNKNOWN' || value === FileLogStatus.UNKNOWN;
              }).join(', ')}
              {item.fileLabel?.resourceType === 'unit' && !item.fileLog?.unit && (
                <span className="text-red-500">Unit ontbreekt</span>
              )}
            </div>
          </TableCell>
        ) : <TableCell onClick={preventSelection}>-</TableCell>;
      case "actions":
        return (
          <div className="flex items-center gap-2 p-2">
            {isEditing ? (
              <>
                <Button
                  isIconOnly
                  size="sm"
                  color="success"
                  onClick={async () => {
                    try {
                      if (localEdits[item.id]) {
                        // await fetchWithAuth(
                        //   `${API_URL}/file/screen`,
                        //   {
                        //     method: "POST",
                        //     headers: { 'Content-Type': 'application/json' },
                        //     body: JSON.stringify({
                        //       sharePointFileId: item.id,
                        //       ...localEdits[item.id],
                        //       acquisitionId: acquisition?.id
                        //     })
                        //   },
                        //   session
                        // );

                        // Remove local edits for this item
                        setLocalEdits(prev => {
                          const newEdits = { ...prev };
                          delete newEdits[item.id];
                          return newEdits;
                        });

                        toast.success('Wijzigingen opgeslagen');
                        await fetchPagedDocuments(pagination[status].page, status);
                      }
                      setEditingRowId(null);
                    } catch (error) {
                      console.error('Error saving changes:', error);
                      toast.error('Kon wijzigingen niet opslaan');
                    }
                  }}
                >
                  <Save className="h-4 w-4" />
                </Button>
                <Button
                  isIconOnly
                  size="sm"
                  color="danger"
                  onClick={() => {
                    // Remove local edits for this item
                    setLocalEdits(prev => {
                      const newEdits = { ...prev };
                      delete newEdits[item.id];
                      return newEdits;
                    });
                    setEditingRowId(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            ) : (
              (status === "unprocessed" || status === "partial") && (
                <Button
                  size="sm"
                  color="primary"
                  startContent={<Plus className="h-4 w-4" />}
                  onClick={() => handleProcessFiles([item])}
                >
                  {status === "unprocessed" ? "Verwerk document" : "Vervolledig"}
                </Button>
              )
            )}
          </div>
        );
      default:
        const value = item[column.key as keyof SharePointItem];
        return value?.toString() || '-';
    }
  };

  // Add cleanup on unmount
  useEffect(() => {
    return () => {
      setLocalEdits({});
    };
  }, []);

  // Add this effect to set default building when there's only one
  useEffect(() => {
    if (buildings?.length === 1 && sharePointFolderId) {
      const defaultBuilding = buildings[0];
      const currentItems = getCurrentItems(currentStatus);

      // Update all items that don't have a building set
      currentItems.forEach(item => {
        if (!item.fileLog?.building) {
          handleCellEdit(item.id, 'building', defaultBuilding);
        }
      });
    }
  }, [buildings, sharePointFolderId]); // Only run when buildings or folderId changes

  // Add a function to check if there are unsaved changes for a specific status
  const hasUnsavedChangesForStatus = (status: 'unprocessed' | 'partial' | 'processed') => {
    const currentItems = getCurrentItems(status);
    return currentItems.some(item => localEdits[item.id]);
  };

  return (
    <>
      <SectionHeader
        title="Documenten"
        type="action"
        icon={<Document />}
        action={
          <a
            href={sharePointFolderUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center"
          >
            <Button
              className="h-fit min-w-fit gap-1 rounded-none bg-transparent p-0 text-sm font-medium text-moss-primary hover:bg-transparent"
            >
              Open map in SharePoint
            </Button>
          </a>
        }
      />

      <div className="flex flex-col gap-2.5 px-3">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            {renderFilesList(unprocessedFiles, "Niet-verwerkte documenten", "unprocessed")}
            {renderFilesList(partiallyProcessedFiles, "Gedeeltelijk verwerkte documenten", "partial")}
            {renderFilesList(processedFiles, "Verwerkte documenten", "processed")}
          </>
        )}
      </div>

      {selectedFile && (
        <ScreenFileModal
          isOpen={isUploadModalOpen}
          onOpenChange={setIsUploadModalOpen}
          onClose={() => {
            setSelectedFile(null);
            setIsUploadModalOpen(false);
            refetchDocuments();
          }}
          file={{
            id: selectedFile.id,
            name: selectedFile.name,
            url: selectedFile.url || selectedFile.webUrl || ""
          }}
          acquisitionId={acquisition?.id}
          buildings={acquisition?.buildings}
        />
      )}
    </>
  );
};

export default AcquisitionDocumentsList;
